import React from "react"

import { Checkbox } from "@coval/ui"

import { useGridCell } from "./useGridCell"

/** Stable marker for the selection cell, so a row can tell it apart from the read-only cells beside it. */
export const SELECT_CELL_ROLE = "select"

interface TestCaseNumberCellProps {
  index: number
  /**
   * 0-based position used for the displayed row number. Defaults to `index`.
   * Differs from `index` when the list is filtered, so the number reflects the
   * test case's position in the full set rather than the filtered view.
   */
  displayIndex?: number
  isMarkedForDeletion: boolean
  isMultiSelected: boolean
  onToggleSelect: (e: React.MouseEvent | React.KeyboardEvent) => void
  width: string
}

export const TestCaseNumberCell: React.FC<TestCaseNumberCellProps> = ({
  index,
  displayIndex,
  isMarkedForDeletion,
  isMultiSelected,
  onToggleSelect,
  width,
}) => {
  const rowNumber = (displayIndex ?? index) + 1
  const { cellProps } = useGridCell({
    ariaLabel: `Select test case ${rowNumber}`,
    mode: "activate",
    onActivate: (e) => onToggleSelect(e as React.KeyboardEvent),
  })

  return (
    <td
      {...cellProps}
      aria-selected={isMultiSelected}
      data-cell-role={SELECT_CELL_ROLE}
      className="group group/select p-1.5 cursor-pointer outline-none"
      style={{ width, verticalAlign: "middle" }}
      onClick={(e) => {
        if (e.shiftKey) {
          e.preventDefault()
          window.getSelection()?.removeAllRanges()
        }
        onToggleSelect(e)
      }}
      onMouseDown={(e) => {
        if (e.shiftKey) {
          e.preventDefault()
        }
      }}
    >
      <div className="relative flex items-center justify-center h-5 rounded">
        {/* Checkbox layer */}
        <div
          className={`absolute inset-0 flex items-center justify-center transition-opacity duration-150 ${
            isMultiSelected
              ? "opacity-100"
              : "opacity-0 group-hover:opacity-100 group-focus-within/select:opacity-100"
          }`}
        >
          <Checkbox
            checked={isMultiSelected}
            className="pointer-events-none"
            aria-label="Select test case"
          />
        </div>

        {/* Number layer */}
        <div
          className={`transition-opacity duration-150 text-sm font-medium select-none ${
            isMultiSelected
              ? "opacity-0"
              : "opacity-100 group-hover:opacity-0 group-focus-within/select:opacity-0"
          } ${
            isMarkedForDeletion
              ? "line-through text-red-600 dark:text-red-400"
              : "text-coval-text"
          }`}
        >
          {rowNumber}
        </div>
      </div>
    </td>
  )
}
